"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import {
  createTronIntent,
  pollTronIntent,
  approveTronPayment,
  completeTronPayment,
  getTronStatus,   // ← add this
} from "@/utils/api/tron";
import { getReferralCode } from "@/hooks/useReferral";

const POLL_INTERVAL_MS = 8_000;
const POLL_MAX_ATTEMPTS = 22; // ~3 min
//  const TOKENS = ["USDT", "USDC"];
const TOKENS = ["USDT"];
const TOKEN_COLOR = { USDT: "#26a17b", USDC: "#2775ca" };

const STAGE = {
  IDLE: "idle",
  LOADING: "loading",
  AWAITING: "awaiting_payment",
  VERIFYING: "verifying",
  DELIVERING: "delivering",
  DONE: "done",
  ERROR: "error",
};

function truncate(str = "", start = 8, end = 6) {
  if (str.length <= start + end + 3) return str;
  return `${str.slice(0, start)}…${str.slice(-end)}`;
}

function copyToClipboard(text) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

function Countdown({ expiresAt }) {
  const [secs, setSecs] = useState(null);
  useEffect(() => {
    const target = new Date(expiresAt).getTime();
    const tick = () => setSecs(Math.max(0, Math.floor((target - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  if (secs === null) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  const urgent = secs < 120;
  return (
    <span className={`tabular-nums font-mono text-xs px-2 py-0.5 rounded ${urgent ? "bg-red-900/50 text-red-300 border border-red-700" : "bg-gray-800 text-gray-400 border border-gray-700"}`}>
      {m}:{String(s).padStart(2, "0")}
    </span>
  );
}

export default function TronPayButton({ product, user, onIntent }) {
  const [stage, setStage] = useState(STAGE.IDLE);
  const [token, setToken] = useState("USDT");
  const [intent, setIntent] = useState(null);
  const [txHash, setTxHash] = useState("");
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [copied, setCopied] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [showManual, setShowManual] = useState(false);
  const [txnId, setTxnId] = useState(null);

  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  const intentRef = useRef(null);
  const productRef = useRef(null);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; clearInterval(pollRef.current); }; }, []);

  useEffect(() => {
    setStage(STAGE.IDLE); setIntent(null); setTxHash(""); setResult(null);
    setErrorMsg(""); setTxnId(null); setShowManual(false); setPollCount(0);
    clearInterval(pollRef.current);
  }, [product?.offerId]);

  const handleCopy = useCallback((text, key) => {
    copyToClipboard(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  }, []);

 const handleDone = useCallback((data) => {
  if (!mountedRef.current) return;
  setResult(data);
  setStage(STAGE.DONE);
  clearInterval(pollRef.current);
}, []);

useEffect(() => {
  if (stage !== STAGE.DELIVERING || !txnId) return;

  const id = setInterval(async () => {
    if (!mountedRef.current) {
      clearInterval(id);
      return;
    }

    try {
      const status = await getTronStatus({ txnId });
      if (!mountedRef.current) return;

      if (status.deliveryStatus === "delivered" || status.status === "complete") {
        clearInterval(id);
        handleDone(status);
      } else if (status.status === "failed" || status.deliveryStatus === "failed") {
        clearInterval(id);
        setErrorMsg("Delivery failed. Please contact support.");
        setStage(STAGE.ERROR);
      }
    } catch (_) {}
  }, 5_000);

  return () => clearInterval(id);
}, [stage, txnId, handleDone]);

  // ── Real auto-poll: calls backend /poll-intent every 8s ─────────────────
  const startPolling = useCallback((intentData, productData) => {
    clearInterval(pollRef.current);
    setPollCount(0);
    intentRef.current = intentData;
    productRef.current = productData;

    let attempts = 0;

    pollRef.current = setInterval(async () => {
      if (!mountedRef.current) return clearInterval(pollRef.current);

      attempts++;
      setPollCount(attempts);

      if (attempts > POLL_MAX_ATTEMPTS) {
        clearInterval(pollRef.current);
        if (mountedRef.current) setShowManual(true);
        return;
      }

      try {
        const referralCode = getReferralCode() ?? undefined;
        const res = await pollTronIntent({
          intent: intentRef.current,
          product: productRef.current,
          referralCode,
        });

        if (!mountedRef.current) return;

        if (res.found) {
          clearInterval(pollRef.current);
          if (res.txnId) setTxnId(res.txnId);

          if (res.deliveryStatus === "delivered" || res.status === "complete") {
            handleDone(res);
          } else {
            // Approved but delivery pending — show delivering state
            setStage(STAGE.DELIVERING);
            if (res.txnId) setTxnId(res.txnId);
          }
        }
        // res.found === false → keep polling silently
      } catch (err) {
        if (!mountedRef.current) return;
        if (err.code === "AUTH") {
          clearInterval(pollRef.current);
          setErrorMsg(err.message);
          setStage(STAGE.ERROR);
        }
        // Other errors (network blip) → keep polling
      }
    }, POLL_INTERVAL_MS);
  }, [handleDone]);

  // ── Step 1: Create intent ────────────────────────────────────────────────
  const handleStart = useCallback(async () => {
    if (!user) { toast.info("Please log in to pay with TRON."); return; }
    setStage(STAGE.LOADING); setErrorMsg("");

    try {
      const data = await createTronIntent({ product, token });
      if (!mountedRef.current) return;
      setIntent(data.intent);
      setStage(STAGE.AWAITING);
      onIntent?.(data.intent);
      startPolling(data.intent, product);
    } catch (err) {
      if (!mountedRef.current) return;
      setErrorMsg(err.message);
      setStage(STAGE.ERROR);
    }
  }, [user, product, token, onIntent, startPolling]);

  // ── Manual fallback: user pastes tx hash ────────────────────────────────
  const handleManualApprove = useCallback(async (hash) => {
    const h = (hash || txHash).trim();
    if (!h) { toast.warn("Please paste your transaction hash."); return; }

    clearInterval(pollRef.current);
    setStage(STAGE.VERIFYING);

    try {
      const referralCode = getReferralCode() ?? undefined;
      const approved = await approveTronPayment({ intent, txHash: h, product, referralCode });
      if (!mountedRef.current) return;

      const id = approved.txnId;
      setTxnId(id);
      setStage(STAGE.DELIVERING);

      const delivered = await completeTronPayment({ txnId: id });
      if (!mountedRef.current) return;
      handleDone(delivered);
    } catch (err) {
      if (!mountedRef.current) return;
      if (err.code === "TRON_NOT_YET") {
        toast.info("Payment not detected yet — waiting…");
        setStage(STAGE.AWAITING);
        setShowManual(true);
      } else {
        setErrorMsg(err.message);
        setStage(STAGE.ERROR);
      }
    }
  }, [txHash, intent, product, handleDone]);

  const accentColor = TOKEN_COLOR[token];

  return (
    <div className="w-full">
      {/* IDLE */}
      {stage === STAGE.IDLE && (
        <div className="rounded-xl border border-gray-700 bg-gray-900/60 p-4 space-y-3">
         <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Pay with</span>
            <span className="px-3 py-1.5 rounded-lg text-xs font-mono font-semibold"
                style={{ background: TOKEN_COLOR.USDT, color: "#fff" }}>
                USDT
            </span>
            <span className="text-xs text-gray-500">· TRC-20 · TRON</span>
            </div>
          <button onClick={handleStart} disabled={!user}
            className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: accentColor, color: "#fff" }}>
            {user ? `Pay with ${token} (TRON)` : "Log in to pay with TRON"}
          </button>
          <p className="text-[11px] text-gray-500 text-center">Send from any wallet · Trust, Binance, OKX, TronLink…</p>
        </div>
      )}

      {/* LOADING */}
      {stage === STAGE.LOADING && (
        <div className="rounded-xl border border-gray-700 bg-gray-900/60 p-6 flex items-center justify-center gap-3">
          <Spinner color={accentColor} />
          <span className="text-sm text-gray-400">Generating payment address…</span>
        </div>
      )}

      {/* AWAITING */}
      {stage === STAGE.AWAITING && intent && (
        <div className="rounded-xl border border-gray-700 bg-gray-900/60 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: accentColor }} />
              <span className="text-sm font-semibold text-gray-200">Send {token} · TRC-20</span>
            </div>
            {intent.expiresAt && <Countdown expiresAt={intent.expiresAt} />}
          </div>

          <div className="flex justify-center">
            <div className="p-3 bg-white rounded-xl shadow-lg">
              <QRCodeSVG value={intent.receivingWallet} size={180} bgColor="#ffffff" fgColor="#111111" level="M" />
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-[11px] text-gray-500 uppercase tracking-widest">Send to address</p>
            <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
              <span className="font-mono text-xs text-gray-200 flex-1 break-all">{intent.receivingWallet}</span>
              <button onClick={() => handleCopy(intent.receivingWallet, "address")} className="text-[11px] shrink-0 transition-colors"
                style={{ color: copied === "address" ? accentColor : "#6b7280" }}>
                {copied === "address" ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-[11px] text-gray-500 uppercase tracking-widest">Exact amount to send</p>
            <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
              <span className="font-mono text-sm font-bold flex-1" style={{ color: accentColor }}>
                {intent.tokenAmount} {token}
              </span>
              <button onClick={() => handleCopy(String(intent.tokenAmount), "amount")} className="text-[11px] shrink-0 transition-colors"
                style={{ color: copied === "amount" ? accentColor : "#6b7280" }}>
                {copied === "amount" ? "Copied!" : "Copy"}
              </button>
            </div>
            <p className="text-[10px] text-amber-400">⚠ Send this exact amount or slightly more — it identifies your payment</p>
          </div>

          <div className="flex items-center gap-2 bg-amber-950/40 border border-amber-800/50 rounded-lg px-3 py-2">
            <span className="text-amber-400 text-base">⚠</span>
            <span className="text-[11px] text-amber-300">TRON network only · Do not send from BSC, ETH or other chains</span>
          </div>

          {/* Auto-poll status */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Spinner color="#6b7280" size={12} />
            <span>
              {showManual
                ? "Auto-detection timed out — paste your tx hash below"
                : `Watching for your transfer… (${pollCount}/${POLL_MAX_ATTEMPTS})`}
            </span>
          </div>

          {/* Manual fallback */}
          {showManual && (
            <div className="space-y-2 pt-1 border-t border-gray-700">
              <p className="text-xs text-gray-400">Paste your transaction hash:</p>
              <div className="flex gap-2">
                <input type="text" value={txHash} onChange={(e) => setTxHash(e.target.value)}
                  placeholder="Transaction ID from your wallet"
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs font-mono text-gray-200 placeholder-gray-600 focus:outline-none focus:border-gray-500" />
                <button onClick={() => handleManualApprove()} disabled={!txHash.trim()}
                  className="px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-40 transition-all"
                  style={{ background: accentColor, color: "#fff" }}>
                  Verify
                </button>
              </div>
            </div>
          )}

          {!showManual && (
            <button onClick={() => setShowManual(true)} className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors w-full text-center">
              Already sent? Paste tx hash manually →
            </button>
          )}

          <button onClick={() => { clearInterval(pollRef.current); setStage(STAGE.IDLE); setIntent(null); }}
            className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors w-full text-center">
            Cancel
          </button>
        </div>
      )}

      {/* VERIFYING */}
      {stage === STAGE.VERIFYING && (
        <div className="rounded-xl border border-gray-700 bg-gray-900/60 p-6 space-y-3">
          <div className="flex items-center gap-3">
            <Spinner color={accentColor} />
            <div>
              <p className="text-sm font-semibold text-gray-200">Verifying on-chain…</p>
              <p className="text-xs text-gray-500">Checking TronGrid for your transfer</p>
            </div>
          </div>
          {txHash && <p className="font-mono text-[10px] text-gray-600 break-all">{truncate(txHash, 12, 10)}</p>}
        </div>
      )}

      {/* DELIVERING */}
      {stage === STAGE.DELIVERING && (
        <div className="rounded-xl border border-gray-700 bg-gray-900/60 p-6 space-y-3">
          <div className="flex items-center gap-3">
            <Spinner color={accentColor} />
            <div>
              <p className="text-sm font-semibold text-gray-200">Delivering your order…</p>
              <p className="text-xs text-gray-500">Payment confirmed · Processing product</p>
            </div>
          </div>
        </div>
      )}

      {/* DONE */}
      {stage === STAGE.DONE && (
        <div className="rounded-xl border p-4 space-y-2"
          style={{ borderColor: accentColor + "55", background: accentColor + "11" }}>
          <div className="flex items-center gap-2">
            <span className="text-lg">✅</span>
            <p className="text-sm font-semibold text-gray-200">Payment complete</p>
          </div>
          <p className="text-xs text-gray-400">
            Check{" "}
            <a href="/my-transactions" className="underline hover:text-gray-200">My Transactions</a>
            {" "}for your delivery details.
          </p>
          {result?.tron?.txHash && (
            <p className="font-mono text-[10px] text-gray-600 break-all">TX: {truncate(result.tron.txHash, 12, 10)}</p>
          )}
        </div>
      )}

      {/* ERROR */}
      {stage === STAGE.ERROR && (
        <div className="rounded-xl border border-red-800/60 bg-red-950/30 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-base">❌</span>
            <p className="text-sm font-semibold text-red-300">Payment failed</p>
          </div>
          <p className="text-xs text-red-400">{errorMsg}</p>
          <button onClick={() => { setStage(STAGE.IDLE); setErrorMsg(""); setIntent(null); setTxHash(""); }}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors underline">
            Try again
          </button>
        </div>
      )}
    </div>
  );
}

function Spinner({ color = "#26a17b", size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="animate-spin shrink-0">
      <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.2" strokeWidth="3" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}